import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useNavigate } from 'react-router-dom';
import UnifiedReadPage, { ImageIdentity } from './UnifiedReadPage';
import {
  formatDate,
  labelize,
  ProviderChips,
  StatusChip,
} from '../../components/workspace/WorkspaceStatus';
import config from '../../config';
import { useAuth } from '../../auth/AuthContext';
import { canonicalRequest } from './CanonicalEditorCommon';

const providerOptions = [
  { value: '', label: 'All providers' },
  { value: 'printify', label: 'Printify' },
  { value: 'printful', label: 'Printful' },
  { value: 'etsy', label: 'Etsy' },
  { value: 'woocommerce', label: 'WooCommerce' },
  { value: 'spoonflower', label: 'Spoonflower' },
];

const providerFilter = { key: 'provider', label: 'Provider', options: providerOptions, minWidth: 170 };

function textCell(value, title = value) {
  return <Typography variant="body2" noWrap title={title || ''}>{value || '—'}</Typography>;
}

function captionCell(primary, secondary) {
  return (
    <Stack sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap title={primary || ''}>{primary || '—'}</Typography>
      {secondary ? <Typography variant="caption" color="text.secondary" noWrap title={secondary}>{secondary}</Typography> : null}
    </Stack>
  );
}

function formatMoney(cents, currency = 'USD') {
  if (!Number.isFinite(Number(cents)) || Number(cents) <= 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(Number(cents) / 100);
}

function activeSalePrice(item) {
  if (!item?.sale_price_cents) return null;
  if (!item.sale_ends_at) return item.sale_price_cents;
  const expiresAt = Date.parse(item.sale_ends_at);
  return Number.isFinite(expiresAt) && expiresAt > Date.now() ? item.sale_price_cents : null;
}

function SpoonflowerGallery({ images = [] }) {
  if (!images.length) return <Typography variant="body2" color="text.secondary">No mockups captured yet.</Typography>;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1 }}>
      {images.map((image, index) => (
        <Paper key={image.id || image.external_id || image.url} variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            component="img"
            src={image.url}
            alt={image.alt_text || `Spoonflower mockup ${index + 1}`}
            loading="lazy"
            sx={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'contain', bgcolor: 'common.white' }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, py: 0.75 }}>
            {labelize(image.context_key || image.role || 'mockup')}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

function SpoonflowerVariants({ variants = [], currency = 'USD' }) {
  if (!variants.length) return <Typography variant="body2" color="text.secondary">No variants captured yet.</Typography>;
  return (
    <Stack spacing={0.75} sx={{ maxHeight: 320, overflow: 'auto' }}>
      {variants.map((variant) => {
        const salePrice = activeSalePrice(variant);
        return (
          <Paper key={variant.id || variant.external_id} variant="outlined" sx={{ p: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2">{variant.title || variant.external_id}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {variant.external_id}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { sm: 'right' }, flexShrink: 0 }}>
                <Typography variant="body2">
                  {formatMoney(salePrice || variant.retail_price_cents, variant.currency || currency)}
                </Typography>
                {salePrice ? (
                  <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    {formatMoney(variant.retail_price_cents, variant.currency || currency)}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

function connectionProviders(item) {
  return (item.fulfillment_instances || []).map((instance) => instance.provider);
}

function publicationProviders(item) {
  return (item.publications || []).map((publication) => publication.provider);
}

function productMockupImage(item) {
  return item.mockup_coverage?.featured_mockup_url
    || item.mockup_coverage?.primary_media_url
    || item.design?.image_url
    || item.catalog_template?.default_image_url;
}

const actionableMockupStatuses = new Set(['ready_to_generate', 'failed']);
const mockupBatchSize = 100;

function ProductMockupSelectionActions({ selectedItems, refresh }) {
  const { fetchWithAuth } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null);
  const eligible = selectedItems.filter((item) => actionableMockupStatuses.has(item.mockup_coverage?.status));
  const missingTemplates = selectedItems.filter((item) => item.mockup_coverage?.status === 'missing_template').length;

  const generate = async () => {
    if (!eligible.length) return;
    setBusy(true);
    setFeedback(null);
    try {
      const productIds = eligible.map((item) => item.id);
      let queued = 0;
      let failures = 0;
      for (let offset = 0; offset < productIds.length; offset += mockupBatchSize) {
        const payload = await canonicalRequest(fetchWithAuth, '/featured-mockups/batch', {
          method: 'POST',
          body: JSON.stringify({ product_ids: productIds.slice(offset, offset + mockupBatchSize) }),
        });
        queued += payload.queued || 0;
        failures += (payload.items || []).filter((item) => item.error).length;
      }
      setFeedback({
        error: failures > 0,
        text: `${queued} featured mockup${queued === 1 ? '' : 's'} queued${failures ? `; ${failures} need attention` : ''}.`,
      });
      await refresh();
    } catch (generateError) {
      setFeedback({ error: true, text: generateError.message || 'Unable to queue featured mockups.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {missingTemplates ? (
        <Typography variant="caption" color="text.secondary">
          {missingTemplates} selected need{missingTemplates === 1 ? 's' : ''} a template first
        </Typography>
      ) : null}
      {feedback ? <Typography variant="caption" color={feedback.error ? 'error.main' : 'success.main'} role="status">{feedback.text}</Typography> : null}
      <Button
        color="inherit"
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeOutlinedIcon />}
        disabled={busy || eligible.length === 0}
        onClick={generate}
      >
        Generate featured ({eligible.length})
      </Button>
    </>
  );
}

function ActivitySteps({ steps = [] }) {
  if (!steps.length) {
    return <Typography variant="body2" color="text.secondary">No workflow steps were recorded.</Typography>;
  }
  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      {steps.map((step, index) => (
        <Paper key={step.id} variant="outlined" sx={{ p: 1.25 }}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              aria-hidden="true"
              sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'surfaceContainerHigh', display: 'grid', placeItems: 'center', flexShrink: 0 }}
            >
              <Typography variant="caption">{index + 1}</Typography>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <Typography variant="body2">{labelize(step.key || step.type)}</Typography>
                <StatusChip status={step.status} />
              </Stack>
              <Typography variant="caption" color="text.secondary">{labelize(step.type)} · {step.attempts || 0} attempts</Typography>
              {step.external_operation ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                  {labelize(step.external_operation.provider)} · {labelize(step.external_operation.action)} · {labelize(step.external_operation.status)}
                </Typography>
              ) : null}
              {step.last_error || step.external_operation?.last_error ? (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
                  {step.last_error || step.external_operation.last_error}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

const activityActionPresentation = {
  pause: { label: 'Pause', icon: <PauseIcon /> },
  resume: { label: 'Resume', icon: <PlayArrowIcon /> },
  retry: { label: 'Retry', icon: <ReplayIcon /> },
  cancel: { label: 'Cancel remaining work', icon: <StopCircleOutlinedIcon /> },
  supersede: { label: 'Supersede run', icon: <SwapHorizIcon /> },
};

function ActivityActions({ item, refresh, close }) {
  const { fetchWithAuth } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState('');
  const actions = Array.isArray(item.allowed_actions) ? item.allowed_actions : [];

  const runAction = async (action) => {
    if ((action === 'cancel' || action === 'supersede') && !window.confirm(
      action === 'cancel'
        ? 'Cancel all remaining work in this workflow? A provider request already in flight may still finish.'
        : 'Supersede this workflow? You will need to preview and apply a new publication plan.',
    )) return;
    setBusy(action);
    setError('');
    try {
      const endpoint = item.kind === 'operation'
        ? `/external-operations/id/${item.id}/retry`
        : `/workflow-runs/${item.id}/${action}`;
      await canonicalRequest(fetchWithAuth, endpoint, { method: 'POST' });
      await refresh();
    } catch (actionError) {
      setError(actionError.message || `Unable to ${action} this activity`);
    } finally {
      setBusy('');
    }
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ width: '100%' }}>
      {error ? <Alert severity="error" sx={{ flex: 1, py: 0 }}>{error}</Alert> : <Box sx={{ flex: 1 }} />}
      {item.kind === 'operation' && item.workflow_run_id ? (
        <Button
          variant="outlined"
          onClick={() => {
            close();
            navigate(`/activity?kind=workflow&q=${item.workflow_run_id}`);
          }}
        >
          Open workflow
        </Button>
      ) : null}
      {actions.map((action) => {
        const presentation = activityActionPresentation[action] || { label: labelize(action) };
        return (
          <Button
            key={action}
            color={action === 'cancel' || action === 'supersede' ? 'warning' : 'primary'}
            variant={action === 'resume' || action === 'retry' ? 'contained' : 'outlined'}
            startIcon={busy === action ? <CircularProgress size={16} color="inherit" /> : presentation.icon}
            disabled={Boolean(busy)}
            onClick={() => runAction(action)}
          >
            {presentation.label}
          </Button>
        );
      })}
      <Button onClick={close} disabled={Boolean(busy)}>Close</Button>
    </Stack>
  );
}

const catalogResource = {
  key: 'catalog',
  endpoint: '/catalog',
  title: 'Catalog',
  description: 'Provider-neutral product types and the fulfillment offers available for each one.',
  itemTitle: (item) => item.name || 'Catalog template',
  itemSubtitle: (item) => item.product_type,
  image: (item) => item.default_image_url,
  filters: [
    providerFilter,
    {
      key: 'status', label: 'Status', minWidth: 160, options: [
        { value: '', label: 'All statuses' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'unknown', label: 'Unknown' },
      ],
    },
  ],
  columns: [
    {
      key: 'template', label: 'Product Type', minWidth: 260, maxWidth: 620, defaultWidth: 360,
      render: (item) => <ImageIdentity src={item.default_image_url} title={item.name} subtitle={item.product_type} />,
    },
    {
      key: 'providers', label: 'Providers', minWidth: 180, maxWidth: 420, defaultWidth: 240,
      render: (item) => <ProviderChips providers={(item.offers || []).map((offer) => offer.provider)} />,
    },
    {
      key: 'offers', label: 'Offers', minWidth: 100, maxWidth: 180, defaultWidth: 110,
      render: (item) => textCell(String((item.offers || []).length)),
    },
    {
      key: 'status', label: 'Status', minWidth: 120, maxWidth: 200, defaultWidth: 140,
      render: (item) => <StatusChip status={item.status} />,
    },
    {
      key: 'updated', label: 'Updated', minWidth: 180, maxWidth: 300, defaultWidth: 200,
      render: (item) => textCell(formatDate(item.updated_at)),
    },
  ],
  detailChips: (item) => [
    { label: labelize(item.status), color: item.status === 'active' ? 'success' : 'default' },
    { label: `${(item.offers || []).length} fulfillment offer${(item.offers || []).length === 1 ? '' : 's'}` },
  ],
  detailFields: (item) => [
    { label: 'Product type', value: item.product_type },
    { label: 'Description', value: item.description },
    { label: 'Providers', value: <ProviderChips providers={(item.offers || []).map((offer) => offer.provider)} /> },
    { label: 'Fulfillment offers', value: (item.offers || []).map((offer) => offer.title).filter(Boolean).join(', ') },
    { label: 'Updated', value: formatDate(item.updated_at) },
  ],
};

const spoonflowerCatalogResource = {
  key: 'spoonflower-provider-products',
  endpoint: '/provider-catalog-items',
  fixedParams: { provider: 'spoonflower' },
  title: 'Spoonflower Products',
  description: 'Read-only Spoonflower products, mockups, variants, and observed retail prices.',
  itemTitle: (item) => item.title || 'Spoonflower product',
  itemSubtitle: (item) => item.product_type,
  image: (item) => item.thumbnail_url || item.images?.[0]?.url,
  viewModes: ['cards', 'table'],
  defaultView: 'cards',
  filters: [
    {
      key: 'product_type', label: 'Product type', minWidth: 190, options: [
        { value: '', label: 'All product types' },
        ...[
          'Fabric', 'Wallpaper', 'Curtain', 'Duvet Cover', 'Napkins', 'Placemat',
          'Pillow Sham', 'Sheet Set', 'Tablecloth', 'Table Runner', 'Tea Towel',
          'Throw Blanket', 'Throw Pillow',
        ].map((value) => ({ value, label: value })),
      ],
    },
    {
      key: 'status', label: 'Status', minWidth: 150, options: [
        { value: '', label: 'All statuses' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'unknown', label: 'Unknown' },
      ],
    },
  ],
  columns: [
    {
      key: 'product', label: 'Design', minWidth: 280, maxWidth: 640, defaultWidth: 380,
      render: (item) => <ImageIdentity src={item.thumbnail_url} title={item.title} subtitle={item.product_type} />,
    },
    {
      key: 'price', label: 'Observed Price', minWidth: 140, maxWidth: 220, defaultWidth: 160,
      render: (item) => captionCell(
        formatMoney(item.sale_price_cents || item.retail_price_cents, item.currency),
        item.sale_price_cents ? `Regular ${formatMoney(item.retail_price_cents, item.currency)}` : '',
      ),
    },
    {
      key: 'images', label: 'Mockups', minWidth: 100, maxWidth: 160, defaultWidth: 110,
      render: (item) => textCell(String(item.image_count || 0)),
    },
    {
      key: 'variants', label: 'Variants', minWidth: 100, maxWidth: 160, defaultWidth: 110,
      render: (item) => textCell(String(item.variant_count || 0)),
    },
    {
      key: 'status', label: 'Status', minWidth: 120, maxWidth: 180, defaultWidth: 130,
      render: (item) => <StatusChip status={item.status} />,
    },
    {
      key: 'observed', label: 'Observed', minWidth: 180, maxWidth: 280, defaultWidth: 190,
      render: (item) => textCell(formatDate(item.observed_at || item.updated_at)),
    },
  ],
  cardFields: (item) => [
    { label: 'Price', value: formatMoney(item.sale_price_cents || item.retail_price_cents, item.currency) },
    { label: 'Mockups', value: String(item.image_count || 0) },
    { label: 'Variants', value: String(item.variant_count || 0) },
  ],
  detailChips: (item) => [
    { label: 'Read only' },
    { label: labelize(item.status), color: item.status === 'active' ? 'success' : 'default' },
    { label: `${item.images?.length || item.image_count || 0} mockup${(item.images?.length || item.image_count || 0) === 1 ? '' : 's'}` },
    { label: `${item.variants?.length || item.variant_count || 0} variant${(item.variants?.length || item.variant_count || 0) === 1 ? '' : 's'}` },
  ],
  detailFields: (item) => [
    { label: 'Product type', value: item.product_type },
    { label: 'Design ID', value: item.external_design_id },
    { label: 'Observed price', value: formatMoney(item.sale_price_cents || item.retail_price_cents, item.currency) },
    { label: 'Regular price', value: item.sale_price_cents ? formatMoney(item.retail_price_cents, item.currency) : '—' },
    { label: 'Canonical design', value: item.design?.filename || 'Not linked' },
    { label: 'Last observed', value: formatDate(item.observed_at || item.updated_at) },
    { label: 'Mockups', value: <SpoonflowerGallery images={item.images || []} /> },
    {
      label: 'Variants',
      value: (
        <SpoonflowerVariants
          variants={item.variants || []}
          currency={item.currency}
        />
      ),
    },
  ],
  detailActions: (item, { close }) => (
    <>
      {item.external_url ? (
        <Button
          component="a"
          href={item.external_url}
          target="_blank"
          rel="noopener noreferrer"
          variant="contained"
          startIcon={<OpenInNewIcon />}
        >
          View on Spoonflower
        </Button>
      ) : null}
      <Button onClick={close}>Close</Button>
    </>
  ),
};

const designsResource = {
  key: 'designs',
  endpoint: '/designs',
  createPath: '/designs/new',
  createLabel: 'New design',
  detailPath: (item) => `/designs/${item.id}`,
  title: 'Designs',
  description: 'Reusable artwork associated with products across fulfillment providers and channels.',
  itemTitle: (item) => item.filename || 'Design',
  image: (item) => item.image_url,
  filters: [
    {
      key: 'status', label: 'Status', minWidth: 160, options: [
        { value: '', label: 'All statuses' },
        { value: 'active', label: 'Active' },
        { value: 'archived', label: 'Archived' },
      ],
    },
  ],
  columns: [
    {
      key: 'design', label: 'Design', minWidth: 300, maxWidth: 720, defaultWidth: 440,
      render: (item) => <ImageIdentity src={item.image_url} title={item.filename} subtitle={item.id} />,
    },
    {
      key: 'dimensions', label: 'Dimensions', minWidth: 140, maxWidth: 240, defaultWidth: 160,
      render: (item) => textCell(item.width && item.height ? `${item.width} × ${item.height}` : '—'),
    },
    {
      key: 'status', label: 'Status', minWidth: 120, maxWidth: 200, defaultWidth: 140,
      render: (item) => <StatusChip status={item.status} />,
    },
    {
      key: 'updated', label: 'Updated', minWidth: 180, maxWidth: 300, defaultWidth: 200,
      render: (item) => textCell(formatDate(item.updated_at)),
    },
  ],
  detailChips: (item) => [
    { label: labelize(item.status), color: item.status === 'active' ? 'success' : 'default' },
    { label: `Version ${item.version || 1}` },
    ...(item.mime_type ? [{ label: item.mime_type }] : []),
  ],
  detailFields: (item) => [
    { label: 'Filename', value: item.filename },
    { label: 'Image ID', value: item.image_id },
    { label: 'Dimensions', value: item.width && item.height ? `${item.width} × ${item.height}` : '—' },
    { label: 'Updated', value: formatDate(item.updated_at) },
  ],
};

function assetImageURL(item) {
  if (item?.image_url) {
    return item.image_url;
  }
  return item?.id && item?.has_local_content ? `${config.apiBaseUrl}/images/${item.id}/content` : '';
}

const assetsResource = {
  key: 'assets',
  endpoint: '/assets',
  createPath: '/upload',
  createLabel: 'Upload asset',
  title: 'Assets',
  description: 'Reusable source images available to designs, products, and listing media.',
  itemTitle: (item) => item.filename || 'Asset',
  image: assetImageURL,
  columns: [
    {
      key: 'asset', label: 'Asset', minWidth: 320, maxWidth: 760, defaultWidth: 460,
      render: (item) => <ImageIdentity src={assetImageURL(item)} title={item.filename} subtitle={item.mime_type} />,
    },
    {
      key: 'dimensions', label: 'Dimensions', minWidth: 140, maxWidth: 240, defaultWidth: 160,
      render: (item) => textCell(item.width && item.height ? `${item.width} × ${item.height}` : '—'),
    },
    {
      key: 'size', label: 'Size', minWidth: 120, maxWidth: 220, defaultWidth: 140,
      render: (item) => textCell(item.size ? `${(item.size / 1024 / 1024).toFixed(2)} MB` : '—'),
    },
    {
      key: 'updated', label: 'Updated', minWidth: 180, maxWidth: 300, defaultWidth: 200,
      render: (item) => textCell(formatDate(item.updated_at)),
    },
  ],
  detailChips: (item) => item.mime_type ? [{ label: item.mime_type }] : [],
  detailFields: (item) => [
    { label: 'Filename', value: item.filename },
    { label: 'Dimensions', value: item.width && item.height ? `${item.width} × ${item.height}` : '—' },
    { label: 'Aspect ratio', value: item.aspect_ratio },
    { label: 'Size', value: item.size ? `${(item.size / 1024 / 1024).toFixed(2)} MB` : '—' },
    { label: 'Remote URL', value: item.image_url },
    { label: 'Updated', value: formatDate(item.updated_at) },
  ],
};

const productsResource = {
  key: 'products',
  endpoint: '/products',
  selectable: true,
  viewModes: ['cards', 'table'],
  defaultView: 'cards',
  cardMinWidth: 286,
  createPath: '/launch',
  createLabel: 'Launch a product',
  detailPath: (item) => `/products/${item.id}`,
  title: 'Products',
  description: 'Stable PODBoss products, independent of the provider shops where they are materialized.',
  itemTitle: (item) => item.name || 'Product',
  itemSubtitle: (item) => item.catalog_template?.name,
  image: productMockupImage,
  renderSelectionActions: (props) => <ProductMockupSelectionActions {...props} />,
  cardFields: (item) => [
    { label: 'Product type', value: item.catalog_template?.name },
    { label: 'Featured mockup', value: <StatusChip status={item.mockup_coverage?.status || 'missing_template'} /> },
    { label: 'Fulfillment', value: <ProviderChips providers={connectionProviders(item)} /> },
    { label: 'Listings', value: (item.listings || []).length },
  ],
  filters: [
    providerFilter,
    {
      key: 'status', label: 'Status', minWidth: 160, options: [
        { value: '', label: 'All statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'ready', label: 'Ready' },
        { value: 'active', label: 'Active' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      key: 'mockup_status', label: 'Featured Mockup', minWidth: 190, options: [
        { value: '', label: 'All coverage' },
        { value: 'ready', label: 'Ready' },
        { value: 'ready_to_generate', label: 'Ready to generate' },
        { value: 'missing_template', label: 'Missing template' },
        { value: 'rendering', label: 'Rendering' },
        { value: 'needs_approval', label: 'Needs approval' },
        { value: 'failed', label: 'Failed' },
      ],
    },
  ],
  columns: [
    {
      key: 'product', label: 'Product', minWidth: 300, maxWidth: 720, defaultWidth: 430,
      render: (item) => (
        <ImageIdentity
          src={productMockupImage(item)}
          title={item.name}
          subtitle={item.design?.filename}
        />
      ),
    },
    {
      key: 'type', label: 'Catalog Type', minWidth: 180, maxWidth: 420, defaultWidth: 240,
      render: (item) => captionCell(item.catalog_template?.name, item.catalog_template?.product_type),
    },
    {
      key: 'providers', label: 'Fulfillment', minWidth: 180, maxWidth: 420, defaultWidth: 240,
      render: (item) => <ProviderChips providers={connectionProviders(item)} />,
    },
    {
      key: 'mockup', label: 'Featured Mockup', minWidth: 170, maxWidth: 260, defaultWidth: 190,
      render: (item) => <StatusChip status={item.mockup_coverage?.status || 'missing_template'} />,
    },
    {
      key: 'status', label: 'Status', minWidth: 120, maxWidth: 200, defaultWidth: 140,
      render: (item) => <StatusChip status={item.status} />,
    },
    {
      key: 'updated', label: 'Updated', minWidth: 180, maxWidth: 300, defaultWidth: 200,
      render: (item) => textCell(formatDate(item.updated_at)),
    },
  ],
  detailChips: (item) => [
    { label: labelize(item.status), color: item.status === 'active' ? 'success' : 'default' },
    { label: `Mockup: ${labelize(item.mockup_coverage?.status || 'missing_template')}` },
    { label: `Version ${item.version || 1}` },
  ],
  detailFields: (item) => [
    { label: 'Design', value: item.design?.filename },
    { label: 'Catalog type', value: item.catalog_template?.name },
    { label: 'Fulfillment providers', value: <ProviderChips providers={connectionProviders(item)} /> },
    { label: 'Fulfillment instances', value: (item.fulfillment_instances || []).length },
    { label: 'Variants', value: (item.variants || []).length },
    { label: 'Listings', value: (item.listings || []).length },
    { label: 'Featured mockup', value: labelize(item.mockup_coverage?.status || 'missing_template') },
    { label: 'Mockup template', value: item.mockup_coverage?.template_name },
    { label: 'Updated', value: formatDate(item.updated_at) },
  ],
};

const listingsResource = {
  key: 'listings',
  endpoint: '/listings',
  selectable: true,
  viewModes: ['cards', 'table'],
  defaultView: 'cards',
  cardMinWidth: 300,
  createPath: '/listings/new',
  createLabel: 'New listing',
  detailPath: (item) => `/listings/${item.id}`,
  renderSelectionActions: ({ selectedItems, navigate }) => (
    <Button
      color="inherit"
      startIcon={<SwapHorizIcon />}
      onClick={() => navigate(`/reconcile?listing_ids=${encodeURIComponent(selectedItems.map((item) => item.id).join(','))}`)}
    >
      Compare &amp; reconcile
    </Button>
  ),
  title: 'Listings',
  description: 'Reusable merchandising drafts and their independent sales-channel publications.',
  itemTitle: (item) => item.title || 'Listing',
  itemSubtitle: (item) => item.product?.name,
  image: (item) => item.featured_media?.source_url || item.product?.design?.image_url,
  cardFields: (item) => [
    { label: 'Channels', value: <ProviderChips providers={publicationProviders(item)} /> },
    { label: 'Publications', value: (item.publications || []).length },
    { label: 'Variants', value: (item.variants || []).length },
    { label: 'Updated', value: formatDate(item.updated_at) },
  ],
  filters: [
    providerFilter,
    {
      key: 'status', label: 'Status', minWidth: 160, options: [
        { value: '', label: 'All statuses' },
        { value: 'draft', label: 'Draft' },
        { value: 'ready', label: 'Ready' },
        { value: 'archived', label: 'Archived' },
      ],
    },
  ],
  columns: [
    {
      key: 'listing', label: 'Listing', minWidth: 340, maxWidth: 840, defaultWidth: 500,
      render: (item) => (
        <ImageIdentity
          src={item.featured_media?.source_url || item.product?.design?.image_url}
          title={item.title}
          subtitle={item.product?.name}
        />
      ),
    },
    {
      key: 'channels', label: 'Channels', minWidth: 190, maxWidth: 440, defaultWidth: 250,
      render: (item) => <ProviderChips providers={publicationProviders(item)} />,
    },
    {
      key: 'publications', label: 'Publications', minWidth: 120, maxWidth: 210, defaultWidth: 140,
      render: (item) => textCell(String((item.publications || []).length)),
    },
    {
      key: 'status', label: 'Draft Status', minWidth: 130, maxWidth: 220, defaultWidth: 150,
      render: (item) => <StatusChip status={item.status} />,
    },
    {
      key: 'updated', label: 'Updated', minWidth: 180, maxWidth: 300, defaultWidth: 200,
      render: (item) => textCell(formatDate(item.updated_at)),
    },
  ],
  detailChips: (item) => [
    { label: labelize(item.status), color: item.status === 'ready' ? 'success' : 'default' },
    { label: `${(item.publications || []).length} publication${(item.publications || []).length === 1 ? '' : 's'}` },
  ],
  detailFields: (item) => [
    { label: 'Product', value: item.product?.name },
    { label: 'Language', value: item.language },
    { label: 'Currency', value: item.currency },
    { label: 'Channels', value: <ProviderChips providers={publicationProviders(item)} /> },
    { label: 'Publication states', value: (item.publications || []).map((publication) => `${labelize(publication.provider)}: ${labelize(publication.status)}`).join(', ') },
    { label: 'Variants', value: (item.variants || []).length },
    { label: 'Media', value: (item.media || []).length },
    { label: 'Updated', value: formatDate(item.updated_at) },
  ],
};

const collectionsResource = {
  key: 'collections',
  endpoint: '/collections',
  title: 'Collections',
  description: 'Local organization that can map to Etsy sections, WooCommerce categories, and future channels.',
  itemTitle: (item) => item.name || 'Collection',
  itemSubtitle: (item) => item.slug,
  filters: [providerFilter],
  columns: [
    {
      key: 'collection', label: 'Collection', minWidth: 260, maxWidth: 600, defaultWidth: 360,
      render: (item) => captionCell(item.name, item.slug),
    },
    {
      key: 'channels', label: 'Channel Mappings', minWidth: 220, maxWidth: 520, defaultWidth: 300,
      render: (item) => <ProviderChips providers={(item.mappings || []).map((mapping) => mapping.provider)} />,
    },
    {
      key: 'mapping_count', label: 'Mappings', minWidth: 100, maxWidth: 180, defaultWidth: 120,
      render: (item) => textCell(String((item.mappings || []).length)),
    },
    {
      key: 'status', label: 'Status', minWidth: 120, maxWidth: 200, defaultWidth: 140,
      render: (item) => <StatusChip status={item.status} />,
    },
    {
      key: 'updated', label: 'Updated', minWidth: 180, maxWidth: 300, defaultWidth: 200,
      render: (item) => textCell(formatDate(item.updated_at)),
    },
  ],
  detailChips: (item) => [{ label: labelize(item.status) }],
  detailFields: (item) => [
    { label: 'Slug', value: item.slug },
    { label: 'Parent', value: item.parent?.name },
    { label: 'Children', value: (item.children || []).map((child) => child.name).join(', ') },
    { label: 'Channel mappings', value: (item.mappings || []).map((mapping) => `${labelize(mapping.provider)}: ${mapping.connection?.display_name}`).join(', ') },
    { label: 'Updated', value: formatDate(item.updated_at) },
  ],
};

const connectionsResource = {
  key: 'connections',
  endpoint: '/connections',
  title: 'Connections',
  description: 'Fulfillment, sales-channel, and media connections with their runtime capabilities and health.',
  itemTitle: (item) => item.display_name || labelize(item.provider),
  itemSubtitle: (item) => `${labelize(item.provider)} · ${labelize(item.kind)}`,
  filters: [
    providerFilter,
    {
      key: 'status', label: 'Status', minWidth: 170, options: [
        { value: '', label: 'All statuses' },
        { value: 'active', label: 'Active' },
        { value: 'disabled', label: 'Disabled' },
        { value: 'needs_auth', label: 'Needs authentication' },
        { value: 'error', label: 'Error' },
      ],
    },
  ],
  columns: [
    {
      key: 'connection', label: 'Connection', minWidth: 260, maxWidth: 620, defaultWidth: 360,
      render: (item) => captionCell(item.display_name, item.external_id),
    },
    {
      key: 'provider', label: 'Provider', minWidth: 130, maxWidth: 240, defaultWidth: 150,
      render: (item) => <Chip size="small" variant="outlined" label={labelize(item.provider)} />,
    },
    {
      key: 'kind', label: 'Type', minWidth: 170, maxWidth: 340, defaultWidth: 220,
      render: (item) => textCell(labelize(item.kind)),
    },
    {
      key: 'status', label: 'Health', minWidth: 140, maxWidth: 220, defaultWidth: 160,
      render: (item) => <StatusChip status={item.active ? item.status : 'disabled'} />,
    },
    {
      key: 'updated', label: 'Updated', minWidth: 180, maxWidth: 300, defaultWidth: 200,
      render: (item) => textCell(formatDate(item.updated_at)),
    },
  ],
  detailChips: (item) => [
    { label: labelize(item.provider) },
    { label: labelize(item.active ? item.status : 'disabled'), color: item.active && item.status === 'active' ? 'success' : 'default' },
  ],
  detailFields: (item) => [
    { label: 'Connection type', value: labelize(item.kind) },
    { label: 'External ID', value: item.external_id },
    { label: 'Source type', value: labelize(item.source_type) },
    { label: 'Capabilities', value: Object.keys(item.capabilities || {}).filter((key) => item.capabilities[key]).join(', ') },
    { label: 'Publication routes', value: (item.routes || []).map((route) => route.name).join(', ') },
    { label: 'Last health check', value: formatDate(item.last_health_check_at) },
    { label: 'Last error', value: item.last_error },
  ],
};

const activityResource = {
  key: 'activity',
  endpoint: '/activity',
  detailEndpoint: (item) => `/activity/${item.kind}/${item.id}`,
  pollInterval: 10000,
  urlFilterKeys: ['resource_type', 'resource_id'],
  title: 'Activity',
  description: 'User goals and durable provider operations in one chronological, provider-neutral view.',
  itemTitle: (item) => labelize(item.action),
  itemSubtitle: (item) => `${labelize(item.kind)} · ${labelize(item.resource_type)}`,
  filters: [
    {
      key: 'kind', label: 'Activity type', minWidth: 170, options: [
        { value: '', label: 'All activity' },
        { value: 'workflow', label: 'Workflows' },
        { value: 'operation', label: 'Provider operations' },
      ],
    },
    providerFilter,
    {
      key: 'status', label: 'Status', minWidth: 170, options: [
        { value: '', label: 'All statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'queued', label: 'Queued' },
        { value: 'running', label: 'Running' },
        { value: 'waiting', label: 'Waiting' },
        { value: 'succeeded', label: 'Succeeded' },
        { value: 'failed', label: 'Failed' },
        { value: 'blocked', label: 'Blocked' },
        { value: 'needs_attention', label: 'Needs attention' },
        { value: 'paused', label: 'Paused' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'superseded', label: 'Superseded' },
      ],
    },
  ],
  columns: [
    {
      key: 'activity', label: 'Activity', minWidth: 280, maxWidth: 640, defaultWidth: 380,
      render: (item) => captionCell(labelize(item.action), labelize(item.kind)),
    },
    {
      key: 'provider', label: 'Provider', minWidth: 130, maxWidth: 240, defaultWidth: 150,
      render: (item) => textCell(labelize(item.provider)),
    },
    {
      key: 'resource', label: 'Resource', minWidth: 240, maxWidth: 560, defaultWidth: 320,
      render: (item) => captionCell(labelize(item.resource_type), item.resource_id),
    },
    {
      key: 'status', label: 'Status', minWidth: 140, maxWidth: 220, defaultWidth: 160,
      render: (item) => <StatusChip status={item.status} />,
    },
    {
      key: 'attempts', label: 'Attempts', minWidth: 100, maxWidth: 170, defaultWidth: 110,
      render: (item) => textCell(String(item.attempts || 0)),
    },
    {
      key: 'updated', label: 'Updated', minWidth: 180, maxWidth: 300, defaultWidth: 200,
      render: (item) => textCell(formatDate(item.updated_at)),
    },
  ],
  detailChips: (item) => [
    { label: labelize(item.kind) },
    { label: labelize(item.status), color: item.status === 'succeeded' ? 'success' : 'default' },
  ],
  detailFields: (item) => [
    { label: 'Provider', value: labelize(item.provider) },
    { label: 'Resource type', value: labelize(item.resource_type) },
    { label: 'Resource ID', value: item.resource_id },
    ...(item.workflow_run_id ? [{ label: 'Owning workflow', value: item.workflow_run_id }] : []),
    { label: 'Field group', value: labelize(item.field_group) },
    { label: 'Attempts', value: item.attempts || 0 },
    { label: 'Next attempt', value: formatDate(item.next_attempt_at) },
    { label: 'Last error', value: item.last_error },
    { label: 'What happens next', value: item.guidance },
    ...(item.steps?.length ? [{ label: 'Workflow progress', value: <ActivitySteps steps={item.steps} /> }] : []),
    ...(item.allowed_actions?.length ? [{ label: 'Available actions', value: item.allowed_actions.map(labelize).join(', ') }] : []),
    { label: 'Created', value: formatDate(item.created_at) },
    { label: 'Completed', value: formatDate(item.completed_at) },
  ],
  detailActions: (item, controls) => <ActivityActions item={item} {...controls} />,
};

export const UnifiedCatalogPage = () => <UnifiedReadPage resource={catalogResource} />;
export const SpoonflowerCatalogPage = () => <UnifiedReadPage resource={spoonflowerCatalogResource} />;
export const UnifiedDesignsPage = () => <UnifiedReadPage resource={designsResource} />;
export const UnifiedAssetsPage = () => <UnifiedReadPage resource={assetsResource} />;
export const UnifiedProductsPage = () => <UnifiedReadPage resource={productsResource} />;
export const UnifiedListingsPage = () => <UnifiedReadPage resource={listingsResource} />;
export const UnifiedCollectionsPage = () => <UnifiedReadPage resource={collectionsResource} />;
export const UnifiedConnectionsPage = () => <UnifiedReadPage resource={connectionsResource} />;
export const UnifiedActivityPage = () => <UnifiedReadPage resource={activityResource} />;
